// src/pages/ApiDocsPage.jsx
// Interactive Swagger-style API documentation page in the broker dashboard.
// Shows all available endpoints, lets broker test them with their own API key.
// Route: /api-docs

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, ChevronDown, ChevronRight, Key, Lock, Zap } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import toast from 'react-hot-toast';

// ─── Endpoint definitions ──────────────────────────────────────────────────
const ENDPOINTS = [
  {
    group: 'Stats',
    color: '#06b6d4',
    endpoints: [
      { method: 'GET', path: '/stats', scope: 'clients:read', description: 'Dashboard summary — clients, accounts, balances, open positions, deposit/withdrawal counts', params: [] },
    ],
  },
  {
    group: 'Clients',
    color: '#6366f1',
    endpoints: [
      { method: 'GET',   path: '/clients',                    scope: 'clients:read', description: 'List all clients with account count', params: [{ name: 'page', type: 'integer', default: '1' }, { name: 'limit', type: 'integer', default: '20' }, { name: 'search', type: 'string' }, { name: 'is_active', type: 'boolean' }, { name: 'kyc_status', type: 'string', enum: ['pending','approved','rejected'] }] },
      { method: 'GET',   path: '/clients/:id',                scope: 'clients:read', description: 'Get single client with balance summary', params: [] },
      { method: 'POST',  path: '/clients',                    scope: 'clients:read', description: 'Create new trader — auto-creates demo and live accounts. Body: email, password, first_name, last_name, phone?, country?', params: [] },
      { method: 'PATCH', path: '/clients/:id',                scope: 'clients:read', description: 'Update client profile. Body: first_name?, last_name?, phone?, country?, kyc_status?, is_active?', params: [] },
      { method: 'POST',  path: '/clients/:id/reset-password', scope: 'clients:read', description: 'Set new password for client (admin action — no current password needed). Body: password', params: [] },
      { method: 'POST',  path: '/clients/:id/enable',         scope: 'clients:read', description: 'Enable a disabled client', params: [] },
      { method: 'POST',  path: '/clients/:id/disable',        scope: 'clients:read', description: 'Disable a client account', params: [] },
    ],
  },
  {
    group: 'Accounts',
    color: '#8b5cf6',
    endpoints: [
      { method: 'GET',  path: '/accounts',          scope: 'accounts:read', description: 'List trading accounts', params: [{ name: 'page', type: 'integer', default: '1' }, { name: 'limit', type: 'integer', default: '20' }, { name: 'account_type', type: 'string', enum: ['live','demo'] }, { name: 'user_id', type: 'integer' }] },
      { method: 'GET',  path: '/accounts/:id',      scope: 'accounts:read', description: 'Get single account with full detail, open positions count, total P&L', params: [] },
      { method: 'POST', path: '/accounts/:id/credit', scope: 'accounts:read', description: 'Credit account balance. Mirrors broker dashboard credit. Fires engine stream + logs account_transactions. Body: amount (positive only), description?. Returns balance_before and balance_after.', params: [] },
      { method: 'POST', path: '/accounts/:id/debit',  scope: 'accounts:read', description: 'Debit account balance. Mirrors broker dashboard debit. Fires engine stream + logs account_transactions. Body: amount (positive only), description?. Returns balance_before and balance_after.', params: [] },
    ],
  },
  {
    group: 'Trades',
    color: '#10b981',
    endpoints: [
      { method: 'GET', path: '/trades',      scope: 'trades:read', description: 'List closed trade history', params: [{ name: 'page', type: 'integer' }, { name: 'limit', type: 'integer' }, { name: 'account_id', type: 'integer' }, { name: 'symbol', type: 'string' }, { name: 'from', type: 'date' }, { name: 'to', type: 'date' }] },
      { method: 'GET', path: '/trades/open', scope: 'trades:read', description: 'List open positions with unrealized P&L', params: [{ name: 'page', type: 'integer' }, { name: 'limit', type: 'integer' }] },
      { method: 'GET', path: '/trades/:id',  scope: 'trades:read', description: 'Get single closed trade by ID', params: [] },
    ],
  },
  {
    group: 'Deposits',
    color: '#f59e0b',
    endpoints: [
      { method: 'GET',  path: '/deposits',            scope: 'deposits:read',  description: 'List deposit requests', params: [{ name: 'status', type: 'string', enum: ['pending','proof_uploaded','approved','rejected'] }, { name: 'user_id', type: 'integer' }] },
      { method: 'POST', path: '/deposits/:id/approve', scope: 'deposits:write', description: 'Approve deposit (status must be proof_uploaded). Credits account balance, logs transaction, notifies engine. Body: notes?. Returns balance_before and balance_after.', params: [] },
      { method: 'POST', path: '/deposits/:id/reject',  scope: 'deposits:write', description: 'Reject deposit (pending or proof_uploaded). No balance change. Body: notes?', params: [] },
    ],
  },
  {
    group: 'Withdrawals',
    color: '#ef4444',
    endpoints: [
      { method: 'GET',  path: '/withdrawals',              scope: 'withdrawals:read',  description: 'List withdrawal requests', params: [{ name: 'status', type: 'string', enum: ['pending','approved','rejected'] }, { name: 'user_id', type: 'integer' }] },
      { method: 'POST', path: '/withdrawals/:id/approve',  scope: 'withdrawals:write', description: 'Approve a pending withdrawal. Balance was already locked at request time. Body: notes?', params: [] },
      { method: 'POST', path: '/withdrawals/:id/reject',   scope: 'withdrawals:write', description: 'Reject a pending withdrawal. Returns locked funds to account balance and notifies engine. Body: notes?', params: [] },
    ],
  },
];

const METHOD_COLORS = {
  GET:    { bg: '#10b981', label: 'GET' },
  POST:   { bg: '#6366f1', label: 'POST' },
  PATCH:  { bg: '#f59e0b', label: 'PATCH' },
  DELETE: { bg: '#ef4444', label: 'DELETE' },
};

// ─── Small copy button ────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-1 rounded transition-all hover:opacity-70">
      {copied
        ? <Check className="w-3.5 h-3.5" style={{ color: 'var(--accent-success)' }} />
        : <Copy className="w-3.5 h-3.5"  style={{ color: 'var(--text-muted)' }} />}
    </button>
  );
}

// ─── Endpoint row ─────────────────────────────────────────────────────────────
function EndpointRow({ endpoint, baseUrl }) {
  const [open, setOpen] = useState(false);
  const mc = METHOD_COLORS[endpoint.method];
  const fullUrl = `${baseUrl}${endpoint.path}`;

  return (
    <div className="border rounded-xl overflow-hidden mb-2"
      style={{ borderColor: 'var(--border-color)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-tertiary)] transition-all"
      >
        <span className="px-2 py-0.5 rounded text-white text-xs font-black w-14 text-center flex-shrink-0"
          style={{ backgroundColor: mc.bg }}>
          {mc.label}
        </span>
        <span className="font-mono text-sm flex-1" style={{ color: 'var(--text-primary)' }}>
          /api/external/v1{endpoint.path}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full border hidden sm:block"
          style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
          {endpoint.scope}
        </span>
        {open
          ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
          : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
      </button>

      {open && (
        <div className="border-t px-4 py-4 space-y-4"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{endpoint.description}</p>

          {/* cURL example */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>
              Example Request
            </p>
            <div className="rounded-lg p-3 font-mono text-xs relative group"
              style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all">
                <CopyButton text={`curl -X ${endpoint.method} "${fullUrl}" \\\n  -H "X-API-Key: ak_live_your_key" \\\n  -H "X-API-Secret: sk_your_secret"`} />
              </div>
              <span style={{ color: '#6366f1' }}>curl</span>
              {' '}-X {endpoint.method}{' '}
              <span style={{ color: '#10b981' }}>"{fullUrl}"</span>{' '}
              \<br />
              {'  '}-H <span style={{ color: '#f59e0b' }}>"X-API-Key: ak_live_your_key"</span>{' '}\<br />
              {'  '}-H <span style={{ color: '#f59e0b' }}>"X-API-Secret: sk_your_secret"</span>
            </div>
          </div>

          {/* Query params */}
          {endpoint.params.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>
                Query Parameters
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ color: 'var(--text-muted)' }}>
                    <th className="text-left pb-1">Name</th>
                    <th className="text-left pb-1">Type</th>
                    <th className="text-left pb-1">Default</th>
                    <th className="text-left pb-1">Values</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.params.map(p => (
                    <tr key={p.name} style={{ color: 'var(--text-secondary)' }}>
                      <td className="py-1 font-mono pr-4" style={{ color: 'var(--accent-primary)' }}>{p.name}</td>
                      <td className="py-1 pr-4">{p.type}</td>
                      <td className="py-1 pr-4">{p.default || '—'}</td>
                      <td className="py-1">{p.enum ? p.enum.join(', ') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── API Keys panel ────────────────────────────────────────────────────────────
function ApiKeysPanel({ keys, onCreateKey, onRevokeKey, loading }) {
  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-8">
          <Key className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No API keys yet</p>
        </div>
      ) : (
        keys.map(k => (
          <div key={k.id} className="flex items-center gap-3 p-3 rounded-xl border"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{k.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <code className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{k.api_key}</code>
                <CopyButton text={k.api_key} />
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(k.scopes || []).map(s => (
                  <span key={s} className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: 'var(--accent-primary)15', color: 'var(--accent-primary)' }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <button onClick={() => onRevokeKey(k.id)}
              className="text-xs px-3 py-1.5 rounded-lg transition-all flex-shrink-0"
              style={{
                backgroundColor: 'var(--accent-danger)15',
                color: 'var(--accent-danger)',
              }}>
              Revoke
            </button>
          </div>
        ))
      )}

      <button onClick={onCreateKey}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed text-sm font-semibold transition-all hover:opacity-80"
        style={{ borderColor: 'var(--accent-primary)50', color: 'var(--accent-primary)' }}>
        <Key className="w-4 h-4" /> Generate New Key
      </button>
    </div>
  );
}

// ─── Create key modal ────────────────────────────────────────────────────────
const ALL_SCOPES = ['clients:read','accounts:read','trades:read','trades:write','deposits:read','deposits:write','withdrawals:read','withdrawals:write'];

function CreateKeyModal({ onClose, onCreated }) {
  const [name, setName]     = useState('');
  const [scopes, setScopes] = useState(ALL_SCOPES);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleScope = (s) => setScopes(prev =>
    prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
  );

  const create = async () => {
    if (!name.trim()) return toast.error('Enter a key name');
    setLoading(true);
    try {
      const res = await fetch('/api/broker/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ name, scopes }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.data);
        onCreated();
      } else {
        toast.error(data.error || 'Failed to create key');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4"
        style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>

        {!result ? (
          <>
            <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>Generate API Key</h3>

            <div>
              <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Key Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Production Key"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: 'var(--text-secondary)' }}>Scopes</label>
              <div className="grid grid-cols-2 gap-1.5">
                {ALL_SCOPES.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggleScope(s)}
                      className="accent-[var(--accent-primary)]" />
                    <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{s}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
                style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                Cancel
              </button>
              <button onClick={create} disabled={loading}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {loading ? 'Generating...' : 'Generate Key'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2"
              style={{ backgroundColor: 'var(--accent-success)20' }}>
              <Check className="w-6 h-6" style={{ color: 'var(--accent-success)' }} />
            </div>
            <h3 className="font-bold text-lg text-center" style={{ color: 'var(--text-primary)' }}>Key Generated</h3>
            <p className="text-xs text-center" style={{ color: 'var(--accent-danger)' }}>
              ⚠️ Copy your secret now — it will never be shown again.
            </p>

            {[['API Key', result.api_key], ['API Secret', result.api_secret]].map(([label, val]) => (
              <div key={label}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <code className="text-xs font-mono flex-1 break-all" style={{ color: 'var(--text-primary)' }}>{val}</code>
                  <CopyButton text={val} />
                </div>
              </div>
            ))}

            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-bold text-white mt-2"
              style={{ backgroundColor: 'var(--accent-primary)' }}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function ApiDocsPage() {
  const navigate = useNavigate();
  const { subscription } = useSubscription();

  const [keys, setKeys]               = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [access, setAccess]           = useState(null); // null=loading

  const baseUrl = `${window.location.protocol}//${window.location.host}/api/external/v1`;

  // ── Check API access on mount ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch('/api/broker/api-keys/access', {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
        });
        const data = await res.json();
        setAccess(data.success ? data.data : { hasAccess: false, via: null });
      } catch {
        setAccess({ hasAccess: false, via: null });
      }
    })();
  }, []);

  const fetchKeys = async () => {
    setKeysLoading(true);
    try {
      const res = await fetch('/api/broker/api-keys', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      });
      const data = await res.json();
      if (data.success) setKeys(data.data);
    } finally {
      setKeysLoading(false);
    }
  };

  const revokeKey = async (id) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    await fetch(`/api/broker/api-keys/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
    });
    toast.success('Key revoked');
    fetchKeys();
  };

  useEffect(() => { fetchKeys(); }, []);

  // ── Loading access check ──────────────────────────────────────────────────
  if (access === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // ── No access — upsell screen ──────────────────────────────────────────────
  if (!access.hasAccess) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-6">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
          style={{ backgroundColor: 'var(--accent-primary)15', border: '2px solid var(--accent-primary)30' }}>
          <Lock className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
        </div>
        <div>
          <h1 className="text-2xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
            REST API Access
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            REST API access is available on the <strong>Enterprise plan</strong> or as a standalone add-on for any plan.
            Connect your systems directly to your broker platform.
          </p>
        </div>
        <div className="rounded-2xl border p-5 text-left space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          {[
            'Read clients, accounts and trade history',
            'Approve deposits and withdrawals programmatically',
            'API key management with scope controls',
            'Up to 60 requests per minute per key',
            'Interactive documentation in your dashboard',
          ].map(f => (
            <div key={f} className="flex items-center gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-success)' }} />
              {f}
            </div>
          ))}
        </div>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate('/upgrade')}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Zap className="w-4 h-4" /> Upgrade to Enterprise
          </button>
          <button onClick={() => navigate('/plugins')}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold border"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            Buy Add-on — $199/mo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* Header — show how access was granted */}
      {access.via && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold w-fit"
          style={{
            backgroundColor: access.via === 'plan' ? 'var(--accent-success)15' : 'var(--accent-primary)15',
            color: access.via === 'plan' ? 'var(--accent-success)' : 'var(--accent-primary)',
          }}>
          <Check className="w-3.5 h-3.5" />
          {access.via === 'plan' ? 'Included in your Enterprise plan' : 'API Access add-on active'}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-all">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
        </button>
        <div>
          <h1 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>REST API</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Base URL: <code className="font-mono">{baseUrl}</code>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left — endpoints */}
        <div className="lg:col-span-2 space-y-6">

          {/* Auth section */}
          <div className="rounded-2xl border p-5"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
              <h2 className="font-bold" style={{ color: 'var(--text-primary)' }}>Authentication</h2>
            </div>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
              All requests require two headers:
            </p>
            <div className="rounded-lg p-3 font-mono text-xs"
              style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
              X-API-Key: <span style={{ color: '#10b981' }}>ak_live_your_api_key</span><br />
              X-API-Secret: <span style={{ color: '#f59e0b' }}>sk_your_secret</span>
            </div>
          </div>

          {/* Endpoint groups */}
          {ENDPOINTS.map(group => (
            <div key={group.group} className="rounded-2xl border overflow-hidden"
              style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
              <button
                onClick={() => setActiveGroup(activeGroup === group.group ? null : group.group)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[var(--bg-tertiary)] transition-all">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }} />
                <span className="font-bold flex-1 text-left" style={{ color: 'var(--text-primary)' }}>
                  {group.group}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {group.endpoints.length} endpoint{group.endpoints.length !== 1 ? 's' : ''}
                </span>
                {activeGroup === group.group
                  ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
              </button>

              {activeGroup === group.group && (
                <div className="border-t px-4 py-4"
                  style={{ borderColor: 'var(--border-color)' }}>
                  {group.endpoints.map(ep => (
                    <EndpointRow key={ep.path + ep.method} endpoint={ep} baseUrl={baseUrl} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right — API keys panel */}
        <div className="space-y-4">
          <div className="rounded-2xl border p-5 sticky top-4"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
              <h2 className="font-bold" style={{ color: 'var(--text-primary)' }}>Your API Keys</h2>
            </div>
            <ApiKeysPanel
              keys={keys}
              loading={keysLoading}
              onCreateKey={() => setShowCreate(true)}
              onRevokeKey={revokeKey}
            />

            <div className="mt-4 pt-4 border-t space-y-1.5"
              style={{ borderColor: 'var(--border-color)' }}>
              {[
                ['Rate limit', `${keys[0]?.rate_limit || 60} req/min`],
                ['Max keys', '10'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { fetchKeys(); }}
        />
      )}
    </div>
  );
}